import { useState, useRef, useEffect, useCallback } from 'react'
import styles from './ScoopTextField.module.scss'
import { ScoopText } from '../ScoopText/ScoopText'

/**
 * ScoopTextField - Native text field component without MUI
 * Replicates the functionality and appearance of Input component
 *
 * @param {string} label - Optional label for the input
 * @param {string} padding - Custom padding for the input (default: '4px')
 * @param {function} onBlur - Callback when input loses focus
 * @param {boolean} disableMargin - If true, removes top margin from container
 * @param {string} className - Additional CSS classes for the input
 * @param {object} containerClassName - Additional CSS classes for the container
 * @param {string} placeholder - Placeholder text
 * @param {string} value - Input value (controlled)
 * @param {function} onChange - Change handler
 * @param {string} type - Input type (text, password, email, etc.)
 * @param {boolean} disabled - If true, input is disabled
 * @param {boolean} readOnly - If true, input is read-only
 * @param {string} name - Input name attribute
 * @param {string} id - Input id attribute
 * @param {boolean} required - If true, input is required
 * @param {boolean} autoFocus - If true, input autofocuses
 * @param {number} maxLength - Maximum input length
 * @param {number} minLength - Minimum input length
 * @param {string} pattern - Validation pattern
 * @param {boolean} multiline - If true, renders as textarea
 * @param {number} rows - Number of rows for textarea
 * @param {number} minRows - Minimum rows for textarea (default: 1)
 * @param {number} maxRows - Maximum rows for textarea (default: 4, only used with autoResize)
 * @param {boolean} autoResize - If true, textarea auto-resizes based on content (default: false)
 */
export const ScoopTextField = ({
    className,
    label,
    padding,
    onBlur,
    disableMargin,
    containerClassName,
    placeholder,
    value,
    onChange,
    type = 'text',
    disabled,
    readOnly,
    name,
    id,
    required,
    autoFocus,
    maxLength,
    minLength,
    pattern,
    multiline,
    rows,
    minRows = 1,
    maxRows = 4,
    autoResize = false,
    style,
    ...props
}) => {
    const [isFocused, setIsFocused] = useState(false)
    const textareaRef = useRef(null)

    // Calculate line height for max height calculation
    const getLineHeight = useCallback((element) => {
        const computedStyle = window.getComputedStyle(element)
        const lineHeight = computedStyle.lineHeight
        if (lineHeight === 'normal') {
            // Approximate line height based on font size
            return parseFloat(computedStyle.fontSize) * 1.2
        }
        return parseFloat(lineHeight)
    }, [])

    // Auto-resize logic - only runs when multiline + autoResize are both true
    useEffect(() => {
        if (!multiline || !autoResize || !textareaRef.current) return

        const textarea = textareaRef.current
        const lineHeight = getLineHeight(textarea)
        const paddingTop = parseFloat(window.getComputedStyle(textarea).paddingTop) || 0
        const paddingBottom = parseFloat(window.getComputedStyle(textarea).paddingBottom) || 0
        const padding = paddingTop + paddingBottom

        const minHeight = lineHeight * minRows + padding
        const maxHeight = lineHeight * maxRows + padding

        // Reset height to calculate scrollHeight correctly
        textarea.style.height = 'auto'
        const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight)
        textarea.style.height = `${newHeight}px`
    }, [value, multiline, autoResize, minRows, maxRows, getLineHeight])

    const handleFocus = (e) => {
        setIsFocused(true)
        if (props.onFocus) props.onFocus(e)
    }

    const handleBlur = (e) => {
        setIsFocused(false)
        if (onBlur) onBlur(e)
    }

    const inputStyle = {
        padding: padding || '',
        ...style,
    }

    const containerClasses = `${styles.inputContainer} ${containerClassName || ''} ${
        disableMargin ? styles.noMargin : ''
    }`

    const inputClasses = [
        styles.input,
        className,
        isFocused && styles.purpleBorderInput,
        disabled && styles.disabled,
        readOnly && styles.readOnly,
        multiline && autoResize && styles.autoResize,
    ]
        .filter(Boolean)
        .join(' ')

    const InputElement = multiline ? 'textarea' : 'input'

    // Only pass rows when not using autoResize (autoResize handles height dynamically)
    const textareaRows = multiline && !autoResize ? rows : undefined

    return (
        <div className={containerClasses}>
            {label && (
                <ScoopText htmlFor={id} className={styles.inputLabel}>
                    {label}
                </ScoopText>
            )}
            <InputElement
                {...props}
                ref={multiline ? textareaRef : undefined}
                type={multiline ? undefined : type}
                className={inputClasses}
                style={inputStyle}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                disabled={disabled}
                readOnly={readOnly}
                name={name}
                id={id}
                required={required}
                autoFocus={autoFocus}
                maxLength={maxLength}
                minLength={minLength}
                pattern={pattern}
                rows={textareaRows}
            />
        </div>
    )
}

ScoopTextField.displayName = 'ScoopTextField'
